import React from 'react';
import { Box, Paper, Typography, Chip, useTheme, alpha } from '@mui/material';
import { ContentCopy, UploadFile, CheckCircle } from '@mui/icons-material';
import { useSiteImportUIStore } from '../../../stores/siteImportUIStore';

const PathSelectionStep: React.FC = () => {
  const theme = useTheme();
  const { importPath, setImportPath } = useSiteImportUIStore();

  const options = [
    {
      id: 'template' as const,
      icon: <ContentCopy sx={{ fontSize: 32 }} />,
      badge: 'Quick start',
      title: 'Use a trade template',
      description:
        'Pick from a ready-made template with example plots and labour items. Great for getting started fast.',
      testId: 'path-template',
    },
    {
      id: 'csv' as const,
      icon: <UploadFile sx={{ fontSize: 32 }} />,
      badge: 'Full control',
      title: 'Upload a CSV file',
      description:
        'Import your own CSV with custom plots and labour items. Use this if you already have your data prepared.',
      testId: 'path-csv',
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        gap: 2,
      }}
      data-testid="path-selection-step"
    >
      {options.map(option => {
        const isSelected = importPath === option.id;

        return (
          <Paper
            key={option.id}
            onClick={() => setImportPath(option.id)}
            sx={{
              p: 3,
              borderRadius: 3,
              cursor: 'pointer',
              position: 'relative',
              border: '2px solid',
              borderColor: isSelected ? 'primary.main' : 'transparent',
              bgcolor: isSelected
                ? alpha(theme.palette.primary.main, 0.04)
                : 'background.paper',
              '&:hover': {
                borderColor: isSelected ? 'primary.main' : 'divider',
              },
              transition: 'all 0.15s ease-in-out',
            }}
            data-testid={option.testId}
          >
            {isSelected && (
              <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
                <CheckCircle color="primary" />
              </Box>
            )}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 1.5,
              }}
            >
              <Box sx={{ color: 'text.secondary' }}>{option.icon}</Box>
              <Chip label={option.badge} size="small" />
            </Box>

            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
              {option.title}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {option.description}
            </Typography>
          </Paper>
        );
      })}
    </Box>
  );
};

export default PathSelectionStep;
